import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SequenceMetadataApiActions, SequenceMetadataUiActions } from '../actions';
import { catchError, map, mergeMap, of } from 'rxjs';
import { ToastService } from '../../../../shared/services/toast.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { SequenceMetadataService } from '../../services/sequence-metadata.service';
import { FileService } from '../../../../shared/services/file.service';

@Injectable()
export class SequenceMetadataEffects {
  private actions$ = inject(Actions);
  private toastService = inject(ToastService);
  private logger = inject(LoggerService);
  private sequenceMetadataService = inject(SequenceMetadataService);
  private fileService = inject(FileService);

  getSequenceMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SequenceMetadataUiActions.GetSequenceMetadata),
      mergeMap(({ sequenceId }) =>
        this.sequenceMetadataService.getSequenceMetadata(sequenceId).pipe(
          map((metadata) => SequenceMetadataApiActions.GetSequenceMetadataSuccess({ payload: metadata })),
          catchError((error) => {
            this.toastService.show(`Error loading sequence metadata: ${error.message || 'Unexpected error'}`, 'error');
            return of(SequenceMetadataApiActions.GetSequenceMetadataFailure({ errors: error }));
          })
        )
      )
    )
  );

  createSequenceMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SequenceMetadataUiActions.CreateSequenceMetadata),
      mergeMap(({ metadata, sequenceId, description, sourceType, gcsFilePath }) => {
        // Build file updates object
        const updates: { description?: string; source_type?: number | null; gcs_file_path?: string } = {};
        if (description !== undefined && description !== null) {
          updates.description = description;
        }
        if (sourceType !== undefined) {
          updates.source_type = sourceType;
        }
        if (gcsFilePath !== undefined && gcsFilePath !== null) {
          updates.gcs_file_path = gcsFilePath;
        }

        // IMPORTANT: Update file FIRST, then associate metadata
        // This ensures source_type is set before associate-metadata endpoint processes tags
        if (Object.keys(updates).length > 0) {
          return this.fileService.updateFile(sequenceId, updates).pipe(
            mergeMap(() => this.sequenceMetadataService.createSequenceMetadata(metadata, sequenceId)),
            map((createdMetadata) => {
              this.toastService.show('Sequence metadata created successfully', 'success');
              return SequenceMetadataApiActions.CreateSequenceMetadataSuccess({ payload: createdMetadata });
            }),
            catchError((error) => {
              const errorMessage = error?.error?.gcs_file_path?.[0] || error?.error?.detail || error.message || 'Unexpected error';
              this.toastService.show(`Error creating sequence metadata: ${errorMessage}`, 'error');
              return of(SequenceMetadataApiActions.CreateSequenceMetadataFailure({ errors: error }));
            })
          );
        }

        // No file updates needed, just create metadata
        return this.sequenceMetadataService.createSequenceMetadata(metadata, sequenceId).pipe(
          map((createdMetadata) => {
            this.toastService.show('Sequence metadata created successfully', 'success');
            return SequenceMetadataApiActions.CreateSequenceMetadataSuccess({ payload: createdMetadata });
          }),
          catchError((error) => {
            this.toastService.show(`Error creating sequence metadata: ${error.message || 'Unexpected error'}`, 'error');
            return of(SequenceMetadataApiActions.CreateSequenceMetadataFailure({ errors: error }));
          })
        );
      })
    )
  );

  promoteToPrimary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SequenceMetadataUiActions.PromoteToPrimary),
      mergeMap(({ metadata, sequenceId, description, sourceType, gcsFilePath }) => {
        // Build file updates object
        const updates: { description?: string; source_type?: number | null; gcs_file_path?: string } = {};
        if (description !== undefined && description !== null) {
          updates.description = description;
        }
        if (sourceType !== undefined) {
          updates.source_type = sourceType;
        }
        if (gcsFilePath !== undefined && gcsFilePath !== null) {
          updates.gcs_file_path = gcsFilePath;
        }

        // IMPORTANT: Update file FIRST, then associate metadata, then promote
        // This ensures source_type is set before associate-metadata endpoint processes tags
        if (Object.keys(updates).length > 0) {
          return this.fileService.updateFile(sequenceId, updates).pipe(
            mergeMap(() => this.sequenceMetadataService.createSequenceMetadata(metadata, sequenceId)),
            mergeMap(() =>
              this.sequenceMetadataService.promoteToPrimary(sequenceId).pipe(
                map((result) => {
                  this.toastService.show('Sequence promoted to primary successfully', 'success');
                  return SequenceMetadataApiActions.PromoteToPrimarySuccess({ payload: result });
                }),
                catchError((error) => {
                  const errorMessage = error?.error?.gcs_file_path?.[0] || error?.error?.detail || error.message || 'Unexpected error';
                  this.toastService.show(`Error promoting to primary: ${errorMessage}`, 'error');
                  return of(SequenceMetadataApiActions.PromoteToPrimaryFailure({ errors: error }));
                })
              )
            ),
            catchError((error) => {
              const errorMessage = error?.error?.gcs_file_path?.[0] || error?.error?.detail || error.message || 'Unexpected error';
              this.toastService.show(`Error: ${errorMessage}`, 'error');
              return of(SequenceMetadataApiActions.PromoteToPrimaryFailure({ errors: error }));
            })
          );
        }

        // No file updates needed, just create metadata and promote
        return this.sequenceMetadataService.createSequenceMetadata(metadata, sequenceId).pipe(
          mergeMap(() =>
            this.sequenceMetadataService.promoteToPrimary(sequenceId).pipe(
              map((result) => {
                this.toastService.show('Sequence promoted to primary successfully', 'success');
                return SequenceMetadataApiActions.PromoteToPrimarySuccess({ payload: result });
              }),
              catchError((error) => {
                const errorMessage = error?.error?.gcs_file_path?.[0] || error?.error?.detail || error.message || 'Unexpected error';
                this.toastService.show(`Error promoting to primary: ${errorMessage}`, 'error');
                return of(SequenceMetadataApiActions.PromoteToPrimaryFailure({ errors: error }));
              })
            )
          ),
          catchError((error) => {
            this.toastService.show(`Error creating sequence metadata: ${error.message || 'Unexpected error'}`, 'error');
            return of(SequenceMetadataApiActions.PromoteToPrimaryFailure({ errors: error }));
          })
        );
      })
    )
  );
}
